import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { COMPARE } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import {
  BarChart3,
  Bot,
  Eye,
  Globe,
  Loader2,
  RefreshCw,
  User,
  Send,
} from "lucide-react";
import OutreachModal from "@/components/OutreachModal";

const ANALYTICS_POLL_INTERVAL_MS = 30_000;

/**
 * Owner-only share analytics panel.
 *
 * @param {string} shareId
 * @param {string} ownerToken
 * @param {ComparisonResult} [result]   Passed through to the outreach modal so
 *   the follow-up template can reference the borrower + region shortlist.
 */
export default function ShareAnalytics({ shareId, ownerToken, result }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [outreachHit, setOutreachHit] = useState(null);

  const load = useCallback(async () => {
    if (!shareId || !ownerToken) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/compare/loan/stats/${shareId}`, {
        headers: { "X-Owner-Token": ownerToken },
      });
      setStats(data);
    } catch (err) {
      // Panel is best-effort — surface the failure to the console for debugging
      // but avoid a toast so a broken analytics call never overwhelms the page.
      console.warn("ShareAnalytics load failed:", err?.response?.data?.detail || err?.message || err);
    } finally {
      setLoading(false);
    }
  }, [shareId, ownerToken]);

  useEffect(() => {
    load();
    // Refresh every 30s while the panel is mounted
    const iv = setInterval(load, ANALYTICS_POLL_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [load]);

  if (!stats) {
    return (
      <div
        data-testid={COMPARE.stats}
        className="rounded-2xl border border-border bg-card p-4 lg:p-5 flex items-center justify-between text-sm text-muted-foreground"
      >
        <span className="inline-flex items-center gap-2">
          <BarChart3 aria-hidden="true" className="w-4 h-4" />
          {loading ? "Loading share analytics…" : "Share this link to start collecting stats."}
        </span>
        {loading && <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" />}
      </div>
    );
  }

  return (
    <div
      data-testid={COMPARE.stats}
      className="rounded-2xl border border-primary/25 bg-primary/[.04] p-5 lg:p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 aria-hidden="true" className="w-4 h-4 text-primary" />
          <h4 className="font-display text-base tracking-tight">
            Share analytics
          </h4>
          <span className="text-[10px] tracking-[0.28em] uppercase text-secondary font-semibold ml-2">
            Owner only
          </span>
        </div>
        <Button
          data-testid={COMPARE.statsRefresh}
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          className="rounded-full text-muted-foreground hover:text-foreground"
        >
          <RefreshCw
            aria-hidden="true"
            className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <StatBox
          icon={Eye}
          label="Total hits"
          value={stats.total_hits}
          testid={COMPARE.statsTotal}
          accent="primary"
        />
        <StatBox
          icon={User}
          label="Human clicks"
          value={stats.human_hits}
          testid={COMPARE.statsHuman}
        />
        <StatBox
          icon={Bot}
          label="Preview fetches"
          value={stats.bot_hits}
          testid={COMPARE.statsBot}
        />
      </div>

      {(stats.by_referrer.length > 0 || stats.by_bot.length > 0) && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          {stats.by_referrer.length > 0 && (
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold flex items-center gap-1.5">
                <Globe aria-hidden="true" className="w-3.5 h-3.5 text-primary" />
                Traffic sources
              </p>
              <ul className="mt-2 space-y-1.5">
                {stats.by_referrer.slice(0, 5).map((r) => (
                  <li
                    key={r.host}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="font-mono text-foreground/85">{r.host}</span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {r.count}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {stats.by_bot.length > 0 && (
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold flex items-center gap-1.5">
                <Bot aria-hidden="true" className="w-3.5 h-3.5 text-primary" />
                Preview crawls
              </p>
              <ul className="mt-2 space-y-1.5">
                {stats.by_bot.slice(0, 5).map((b) => (
                  <li
                    key={b.name}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-foreground/85">{b.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {b.count}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {stats.recent.length > 0 && (
        <div className="mt-5 border-t border-border pt-4">
          <button
            type="button"
            onClick={() => setExpanded((s) => !s)}
            className="text-[10px] tracking-[0.28em] uppercase text-primary font-semibold hover:underline"
          >
            {expanded ? "Hide" : "Show"} recent {stats.recent.length} hit
            {stats.recent.length === 1 ? "" : "s"}
          </button>
          {expanded && (
            <ul className="mt-3 space-y-1.5 text-xs font-mono text-muted-foreground">
              {stats.recent.map((h, i) => (
                <li
                  key={h.ts + h.kind + (h.bot_name || h.ref_host || "")}
                  className="flex items-center gap-3"
                >
                  <span className="w-32 shrink-0">
                    {new Date(h.ts).toLocaleString()}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full border text-[10px] ${
                      h.kind === "human"
                        ? "border-primary/40 text-primary"
                        : "border-secondary/40 text-secondary"
                    }`}
                  >
                    {h.kind}
                  </span>
                  <span className="text-foreground/70 flex-1">
                    {h.kind === "human" ? h.ref_host || "direct" : h.bot_name}
                  </span>
                  {h.kind === "human" && result && (
                    <button
                      type="button"
                      data-testid={COMPARE.outreachOpen(i)}
                      onClick={() => setOutreachHit(h)}
                      className="inline-flex items-center gap-1 rounded-full border border-primary/30 px-2.5 py-0.5 text-[10px] font-sans text-primary hover:bg-primary/10 transition-colors"
                    >
                      <Send aria-hidden="true" className="w-3 h-3" />
                      Reach out
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <OutreachModal
        open={!!outreachHit}
        onOpenChange={(v) => !v && setOutreachHit(null)}
        result={result}
        hit={outreachHit}
        shareId={shareId}
        ownerToken={ownerToken}
      />
    </div>
  );
}

function StatBox({ icon: Icon, label, value, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-3 flex flex-col gap-1 ${
        accent === "primary"
          ? "border-primary/30 bg-primary/[.06]"
          : "border-border bg-background"
      }`}
    >
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
        <Icon aria-hidden="true" className="w-3 h-3" />
        {label}
      </div>
      <div className="font-mono text-2xl text-primary">{value}</div>
    </div>
  );
}
